import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import VisualizerProgress from '../../components/navigation/VisualizerProgress';
import SelectionSummary from '../../components/navigation/SelectionSummary';
import CompanyBranding from '../../components/navigation/CompanyBranding';
import FloorTypeCard from './components/FloorTypeCard';
import ColorSwatch from './components/ColorSwatch';
import RoomCard from './components/RoomCard';
import IntensitySlider from './components/IntensitySlider';
import FinishSelector from './components/FinishSelector';
import LivePreview from './components/LivePreview';
import ReferenceCodeGenerator from './components/ReferenceCodeGenerator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { companyService } from '../../services/companyService';

const VisualizerFlow = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selections, setSelections] = useState({
    floorType: null,
    baseColor: null,
    flakeColors: [],
    finish: 'glossy',
    room: null,
    intensity: 50,
    heaviness: 50
  });

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyIdentifier) {
        setError('Company identifier is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Extract slug from compound identifier (format: slug-uuid)
        // Example: "floortech-epoxy-solutions-eb72a386-771b-41ab-bbb3-e1f5c6421e67"
        // Should extract: "floortech-epoxy-solutions"
        const extractSlugFromIdentifier = (identifier) => {
          // UUID pattern: 8-4-4-4-12 characters separated by dashes
          // We need to find where the UUID starts and take everything before it
          const parts = identifier?.split('-');

          // Find the index where UUID starts (first part that matches UUID segment pattern)
          let uuidStartIndex = -1;
          for (let i = parts?.length - 1; i >= 0; i--) {
            // UUID last segment is 12 chars, check if we have valid UUID pattern
            if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
              // Found potential UUID end, check if previous 4 parts match UUID pattern
              if (i >= 4 &&
              parts?.[i - 1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 1]) &&
              parts?.[i - 2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 2]) &&
              parts?.[i - 3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 3]) &&
              parts?.[i - 4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i - 4])) {
                uuidStartIndex = i - 4;
                break;
              }
            }
          }

          // If UUID found, return everything before it, otherwise return full identifier
          if (uuidStartIndex > 0) {
            return parts?.slice(0, uuidStartIndex)?.join('-');
          }

          return identifier;
        };

        const slug = extractSlugFromIdentifier(companyIdentifier);
        const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();

        console.log('Visualizer: Original identifier:', companyIdentifier);
        console.log('Visualizer: Extracted slug:', slug);
        console.log('Visualizer: Normalized slug:', normalizedSlug);

        // Fetch company data from Supabase using slug
        const companyData = await companyService?.getBySlug(normalizedSlug);

        if (!companyData) {
          setError(`Company not found with slug: ${normalizedSlug}`);
          setCompany(null);
        } else {
          console.log('Visualizer: Company loaded successfully:', companyData?.name);
          setCompany(companyData);
        }
      } catch (err) {
        console.error('Error fetching company:', err);
        setError(err?.message || 'Failed to load company data');
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companyIdentifier]);

  const mockFloorTypes = [
  {
    id: 1,
    name: 'Metallic Epoxy',
    description: 'Stunning metallic finish with unique patterns and depth that creates a luxurious, three-dimensional appearance',
    price: 8.50,
    unit: 'sq ft',
    thumbnail: "https://images.unsplash.com/photo-1731945087778-d6036b46cec3",
    thumbnailAlt: 'Luxurious metallic epoxy flooring with swirling silver and blue patterns creating depth and dimension in modern interior space'
  },
  {
    id: 2,
    name: 'Flake Epoxy',
    description: 'Decorative colored flakes provide texture, slip resistance, and customizable aesthetic appeal for any space',
    price: 6.75,
    unit: 'sq ft',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
    thumbnailAlt: 'Textured flake epoxy flooring with multicolored decorative chips scattered across glossy gray base in commercial garage setting'
  },
  {
    id: 3,
    name: 'Countertop Epoxy',
    description: 'Food-safe, heat-resistant epoxy perfect for kitchen and bathroom countertops with seamless finish',
    price: 12.00,
    unit: 'sq ft',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9889b36-1765159449018.png",
    thumbnailAlt: 'Smooth glossy epoxy countertop in modern kitchen with white cabinets and stainless steel appliances showing seamless finish'
  },
  {
    id: 4,
    name: 'Solid Color Epoxy',
    description: 'Clean, uniform color coating ideal for industrial and commercial applications requiring durability',
    price: 5.50,
    unit: 'sq ft',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5ce5ddd-1764775753935.png",
    thumbnailAlt: 'Uniform solid gray epoxy floor coating in industrial warehouse with clean lines and high-gloss reflective surface'
  }];


  const mockColors = [
  { id: 1, name: 'Midnight Blue', hex: '#1e3a8a', swatchImage: 'https://via.placeholder.com/100/1e3a8a', swatchImageAlt: 'Deep midnight blue color swatch' },
  { id: 2, name: 'Pearl White', hex: '#f8fafc', swatchImage: 'https://via.placeholder.com/100/f8fafc', swatchImageAlt: 'Elegant pearl white color swatch' },
  { id: 3, name: 'Charcoal Gray', hex: '#374151', swatchImage: 'https://via.placeholder.com/100/374151', swatchImageAlt: 'Modern charcoal gray color swatch' },
  { id: 4, name: 'Copper Bronze', hex: '#b45309', swatchImage: 'https://via.placeholder.com/100/b45309', swatchImageAlt: 'Warm copper bronze metallic color swatch' },
  { id: 5, name: 'Emerald Green', hex: '#059669', swatchImage: 'https://via.placeholder.com/100/059669', swatchImageAlt: 'Rich emerald green color swatch' },
  { id: 6, name: 'Ruby Red', hex: '#dc2626', swatchImage: 'https://via.placeholder.com/100/dc2626', swatchImageAlt: 'Bold ruby red color swatch' },
  { id: 7, name: 'Silver Mist', hex: '#94a3b8', swatchImage: 'https://via.placeholder.com/100/94a3b8', swatchImageAlt: 'Soft silver mist color swatch' },
  { id: 8, name: 'Gold Shimmer', hex: '#f59e0b', swatchImage: 'https://via.placeholder.com/100/f59e0b', swatchImageAlt: 'Luxurious gold shimmer metallic color swatch' }];


  const mockRooms = [
  {
    id: 1,
    name: 'Modern Garage',
    description: 'Spacious two-car garage with clean lines and ample natural light',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
    backgroundImageAlt: 'Clean modern two-car garage interior with white walls, organized storage, and bright natural lighting from windows'
  },
  {
    id: 2,
    name: 'Industrial Warehouse',
    description: 'Large commercial space with high ceilings and industrial aesthetic',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecdfb29a-1764699974639.png",
    backgroundImageAlt: 'Expansive industrial warehouse interior with high ceilings, exposed beams, and concrete floors in open layout'
  },
  {
    id: 3,
    name: 'Residential Basement',
    description: 'Finished basement perfect for recreation and entertainment areas',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_130e0ce7e-1764638986186.png",
    backgroundImageAlt: 'Finished residential basement with comfortable seating area, entertainment center, and warm ambient lighting'
  },
  {
    id: 4,
    name: 'Retail Showroom',
    description: 'Professional retail space with modern design and customer flow',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_117687d8f-1764642188778.png",
    backgroundImageAlt: 'Contemporary retail showroom with product displays, modern fixtures, and polished floors in bright commercial space'
  }];


  const handleFloorTypeSelect = (floorType) => {
    setSelections((prev) => ({ ...prev, floorType }));
  };

  const handleBaseColorSelect = (color) => {
    setSelections((prev) => ({ ...prev, baseColor: color }));
  };

  const handleFlakeColorSelect = (color) => {
    setSelections((prev) => {
      const isSelected = prev?.flakeColors?.some((c) => c?.id === color?.id);
      if (isSelected) {
        return {
          ...prev,
          flakeColors: prev?.flakeColors?.filter((c) => c?.id !== color?.id)
        };
      } else if (prev?.flakeColors?.length < 5) {
        return {
          ...prev,
          flakeColors: [...prev?.flakeColors, color]
        };
      }
      return prev;
    });
  };

  const handleRoomSelect = (room) => {
    setSelections((prev) => ({ ...prev, room }));
  };

  const handleNext = () => {
    if (currentStep === 1 && !selections?.floorType) {
      alert('Please select a floor type to continue');
      return;
    }
    if (currentStep === 2 && !selections?.baseColor) {
      alert('Please select at least a base color to continue');
      return;
    }
    if (currentStep === 3 && !selections?.room) {
      alert('Please select a room to continue');
      return;
    }

    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || completedSteps?.includes(stepId)) {
      setCurrentStep(stepId);
    }
  };

  const handleStartOver = () => {
    setSelections({
      floorType: null,
      baseColor: null,
      flakeColors: [],
      finish: 'glossy',
      room: null,
      intensity: 50,
      heaviness: 50
    });
    setCurrentStep(1);
    setCompletedSteps([]);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-text-primary mb-3">
                Choose Your Floor Type
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Select the epoxy flooring system that best fits your project needs and budget
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockFloorTypes?.map((floorType) =>
              <FloorTypeCard
                key={floorType?.id}
                floorType={floorType}
                isSelected={selections?.floorType?.id === floorType?.id}
                onSelect={handleFloorTypeSelect} />

              )}
            </div>
          </div>);


      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-text-primary mb-3">
                Select Your Colors
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Choose a base color and up to 4 additional flake colors to create your custom design
              </p>
            </div>
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">
                  Base Color (Required)
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                  {mockColors?.map((color) =>
                  <ColorSwatch
                    key={color?.id}
                    color={color}
                    isSelected={selections?.baseColor?.id === color?.id}
                    onSelect={handleBaseColorSelect}
                    disabled={false} />

                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-text-primary">
                    Flake Colors (Optional)
                  </h3>
                  <span className="text-sm text-text-secondary">
                    {selections?.flakeColors?.length} / 5 selected
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                  {mockColors?.map((color) =>
                  <ColorSwatch
                    key={color?.id}
                    color={color}
                    isSelected={selections?.flakeColors?.some((c) => c?.id === color?.id)}
                    onSelect={handleFlakeColorSelect}
                    disabled={selections?.flakeColors?.length >= 5 && !selections?.flakeColors?.some((c) => c?.id === color?.id)} />

                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <FinishSelector
                  selectedFinish={selections?.finish}
                  onFinishChange={(finish) => setSelections((prev) => ({ ...prev, finish }))} />

                <div className="space-y-4">
                  <IntensitySlider
                    label="Color Intensity"
                    value={selections?.intensity}
                    onChange={(value) => setSelections((prev) => ({ ...prev, intensity: value }))}
                    icon="Droplet" />

                  <IntensitySlider
                    label="Design Heaviness"
                    value={selections?.heaviness}
                    onChange={(value) => setSelections((prev) => ({ ...prev, heaviness: value }))}
                    icon="Layers" />

                </div>
              </div>
            </div>
          </div>);


      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-text-primary mb-3">
                Choose Your Room
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Select a room template to visualize your epoxy flooring design
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockRooms?.map((room) =>
              <RoomCard
                key={room?.id}
                room={room}
                isSelected={selections?.room?.id === room?.id}
                onSelect={handleRoomSelect} />

              )}
            </div>
          </div>);


      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-text-primary mb-3">
                Preview & Finalize
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Review your design and generate your reference code
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              <LivePreview
                room={selections?.room}
                colors={[selections?.baseColor, ...selections?.flakeColors]?.filter(Boolean)}
                intensity={selections?.intensity}
                heaviness={selections?.heaviness}
                finish={selections?.finish} />

              <ReferenceCodeGenerator selections={selections} />
            </div>
          </div>);


      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading visualizer...</p>
        </div>
      </div>);

  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            {error ? 'Error Loading Company' : 'Company Not Found'}
          </h2>
          <p className="text-text-secondary mb-6">
            {error || 'The company you\'re looking for doesn\'t exist or has been deactivated.'}
          </p>
          <Button variant="default" onClick={() => navigate('/')}>
            Return Home
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <CompanyBranding company={company} />
      <VisualizerProgress
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick} />

      <div className="relative">
        <div className="lg:pr-96">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {renderStepContent()}

            <div className="flex items-center justify-between mt-12 pt-8 border-t border-border">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1}
                iconName="ChevronLeft"
                iconPosition="left"
                iconSize={18}>

                Back
              </Button>

              <div className="flex gap-3">
                {currentStep === 4 ?
                <Button
                  variant="outline"
                  onClick={handleStartOver}
                  iconName="RotateCcw"
                  iconPosition="left"
                  iconSize={18}>

                    Start Over
                  </Button> :

                <Button
                  variant="default"
                  onClick={handleNext}
                  iconName="ChevronRight"
                  iconPosition="right"
                  iconSize={18}>

                    {currentStep === 3 ? 'Preview Design' : 'Continue'}
                  </Button>
                }
              </div>
            </div>
          </div>
        </div>

        <SelectionSummary
          selections={{
            floorType: selections?.floorType,
            baseColor: selections?.baseColor,
            flakeColor: selections?.flakeColors?.[0],
            room: selections?.room
          }}
          onEdit={(key) => {
            const stepMap = {
              floorType: 1,
              baseColor: 2,
              flakeColor: 2,
              room: 3
            };
            setCurrentStep(stepMap?.[key]);
          }}
          onClear={handleStartOver} />


        <button
          onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
          className="lg:hidden fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center z-[900]">

          <Icon name={isMobileSummaryOpen ? 'X' : 'List'} size={24} />
        </button>

        {isMobileSummaryOpen &&
        <div className="lg:hidden fixed inset-0 bg-background/95 backdrop-blur-sm z-[850] overflow-y-auto p-6">
            <div className="max-w-md mx-auto">
              <SelectionSummary
              selections={{
                floorType: selections?.floorType,
                baseColor: selections?.baseColor,
                flakeColor: selections?.flakeColors?.[0],
                room: selections?.room
              }}
              onEdit={(key) => {
                const stepMap = {
                  floorType: 1,
                  baseColor: 2,
                  flakeColor: 2,
                  room: 3
                };
                setCurrentStep(stepMap?.[key]);
                setIsMobileSummaryOpen(false);
              }}
              onClear={() => {
                handleStartOver();
                setIsMobileSummaryOpen(false);
              }} />

            </div>
          </div>
        }
      </div>
    </div>);

};

export default VisualizerFlow;